import { getOrderRefundAmount } from '../../utilities/helpers'

const MONEY_FIELDS_TO_NEGATE = [
  'basePrice',
  'offerPrice',
  'facilityFee',
  'salesTax',
  'donationTotal',
  'gross',
  'serviceFee',
  'processingFee',
  'estimatedProcessingFee',
  'chargebackProtection',
  'totalFeeAmount',
  'destinationAccountNet',
  'net',
  'redemptionValue',
  'totalDiscount',
  'amountRemaining',
  'resaleOrganizerShare',
  'resaleSellerPayout',
  'resalePlatformShare',
]

const COUNT_FIELDS_TO_NEGATE = ['quantity', 'packageTicketsCount']

const roundMoney = (n) => Math.round(n * 100) / 100

export const isFullyRefundedListing = (listing) =>
  String(listing?.status || '').toLowerCase() === 'refunded'

export const isOrderListingRefundRow = (listing) =>
  listing?.isOrderListingRefundRow === true

const isTruthyFlag = (value) =>
  value === true ||
  value === 1 ||
  value === '1' ||
  value === 't' ||
  value === 'true'

/**
 * Ticket/seat removal already wrote the refund into gross, net, and fees.
 * A second negative listings line would subtract that refund again.
 */
export const isPartialRefundReflectedInListingTotals = (listing) =>
  !isFullyRefundedListing(listing) &&
  isTruthyFlag(listing?.partialRefundReflectedInTotals)

/**
 * Scale for the refund reverse line.
 * Full refunds reverse 100% (net 0 with the sale line).
 * Partial refunds reverse refundAmount / gross.
 */
export const getOrderListingRefundScale = (listing) => {
  if (isFullyRefundedListing(listing)) return 1

  const refundAmount = Math.abs(getOrderRefundAmount(listing) || 0)
  if (refundAmount <= 0) return 0

  const gross = Math.abs(Number(listing?.gross) || 0)
  if (gross > 0) return Math.min(1, refundAmount / gross)

  return 1
}

export const shouldExpandListingWithRefundRow = (listing) => {
  if (!listing || isOrderListingRefundRow(listing)) return false
  if (isFullyRefundedListing(listing)) return true
  if (isPartialRefundReflectedInListingTotals(listing)) return false
  return getOrderRefundAmount(listing) > 0
}

const negateMoneyField = (value, scale) => {
  if (value === null || value === undefined || value === '') return value
  const n = Number(value)
  if (!Number.isFinite(n) || n === 0) return 0
  return roundMoney(-Math.abs(n) * scale)
}

const negateCountField = (value, scale, fullyRefunded) => {
  if (value === null || value === undefined || value === '') return value
  const n = Number(value)
  if (!Number.isFinite(n) || n === 0) return 0
  if (fullyRefunded) return -Math.abs(n)
  const scaled = Math.round(Math.abs(n) * scale)
  return scaled > 0 ? -scaled : 0
}

/**
 * Build the negative refund companion row for a sale listing.
 * Qty and money fields are negated so a full refund nets to 0 with the sale line.
 */
export const buildOrderListingRefundRow = (listing) => {
  const scale = getOrderListingRefundScale(listing)
  if (scale <= 0) return null

  const fullyRefunded = isFullyRefundedListing(listing)
  const refundRow = {
    ...listing,
    isOrderListingRefundRow: true,
    orderListingRefundScale: scale,
    packageTicketsCountBeforeRefund: listing.packageTicketsCount,
    // Keep refund amount on the sale line only so the column does not double-count.
    refundAmount: 0,
  }

  for (const field of MONEY_FIELDS_TO_NEGATE) {
    if (Object.prototype.hasOwnProperty.call(listing, field)) {
      refundRow[field] = negateMoneyField(listing[field], scale)
    }
  }

  for (const field of COUNT_FIELDS_TO_NEGATE) {
    if (Object.prototype.hasOwnProperty.call(listing, field)) {
      refundRow[field] = negateCountField(listing[field], scale, fullyRefunded)
    }
  }

  return refundRow
}

/**
 * Expand refunded / partially refunded orders into sale + refund lines.
 * Sale stays positive; refund line carries negative qty and $.
 */
export const expandOrderListingsWithRefundRows = (listings = []) => {
  const out = []
  for (const listing of listings || []) {
    out.push(listing)
    if (!shouldExpandListingWithRefundRow(listing)) continue
    const refundRow = buildOrderListingRefundRow(listing)
    if (refundRow) out.push(refundRow)
  }
  return out
}
